import {
  ChangeEventHandler,
  Context,
  createContext,
  Dispatch,
  FC,
  FormEvent,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import { Dialog, Loader, SearchableSelect, useDialog } from 'components';
import { Booking, CreatedBooking, CreatedBookingDetails, RequiredBooking, sslScacs } from 'types';
import { useSizeTypes } from 'contexts';
import { isReeferOrGenset } from 'utils';

import useTimeZone from 'hooks/use-timezone';
import './new-booking.scss';

interface T_GateNewBookingContext {
  findNewBooking: (bookingNumber: string, customerId: number, locationId: number) => Booking | null;
  setBookings: Dispatch<SetStateAction<RequiredBooking[]>>;
  newBooking?: CreatedBooking;
  setNewBooking: Dispatch<SetStateAction<CreatedBooking | undefined>>;
  editedBooking?: Booking;
  setEditedBooking: Dispatch<SetStateAction<Booking | undefined>>;
  postProcessFn: () => void;
  setPostProcessFn: Dispatch<SetStateAction<() => void>>;
}

const GateNewBookingContext: Context<T_GateNewBookingContext> = createContext<T_GateNewBookingContext>({
  findNewBooking: () => null,
  setBookings: () => {},
  setNewBooking: () => {},
  setEditedBooking: () => {},
  postProcessFn: () => {},
  setPostProcessFn: () => {}
});

export const useGateNewBooking = (): T_GateNewBookingContext => useContext(GateNewBookingContext);

export const GateNewBookingProvider: FC<PropsWithChildren> = ({ children }) => {
  const [bookings, setBookings] = useState<RequiredBooking[]>([]);
  const [newBooking, setNewBooking] = useState<CreatedBooking>();
  const [editedBooking, setEditedBooking] = useState<Booking>();
  const [postProcessFn, setPostProcessFn] = useState<() => void>(() => {});

  // useEffect(() => console.log(postProcessFn), [postProcessFn]);

  const findNewBooking = useCallback(
    (bookingNumber: string, customerId: number, locationId: number): Booking | null => {
      return (
        bookings.find(
          booking =>
            booking.bookingNumber === bookingNumber &&
            booking.customer.id === customerId &&
            booking.location.id === locationId
        ) ?? null
      );
    },
    [bookings]
  );

  return (
    <GateNewBookingContext.Provider
      value={{
        findNewBooking,
        newBooking,
        setBookings,
        editedBooking,
        setEditedBooking,
        setNewBooking,
        postProcessFn,
        setPostProcessFn
      }}
    >
      {children}
    </GateNewBookingContext.Provider>
  );
};

export const GateNewBooking: FC = () => {
  const { closeDialog, openDialog, ref } = useDialog();
  const sizeTypeCatalog = useSizeTypes();
  const { newBooking, setBookings, setNewBooking, postProcessFn, setPostProcessFn } = useGateNewBooking();
  const { dateInputToUtc, utcToZonedDate } = useTimeZone();

  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const bookingDetails0: CreatedBookingDetails = useMemo(() => {
    return newBooking?.bookingDetails?.[0] ?? new CreatedBookingDetails(['00']);
  }, [newBooking?.bookingDetails]);

  useEffect(() => {
    if (newBooking && !ref.current?.open) {
      openDialog();
    } else if (!newBooking && ref.current?.open) {
      closeDialog();
    }
  }, [newBooking, ref, openDialog, closeDialog]);

  const handleChangeQuantity: ChangeEventHandler<HTMLInputElement> = useCallback(
    e => {
      const count = Math.max(1, Math.min(1000, Number(e.target.value)));
      if (!isNaN(count)) {
        setNewBooking(b => b && { ...b, bookingDetails: [{ ...bookingDetails0, count }] });
      }
    },
    [bookingDetails0, setNewBooking]
  );

  const handleStepChange = (step: -1 | 1) => {
    const count = Math.max(1, Math.min(1000, bookingDetails0.count + step));
    setNewBooking(b => b && { ...b, bookingDetails: [{ ...bookingDetails0, count }] });
  };

  const handleReset = useCallback(() => {
    setPostProcessFn(() => {});
    setNewBooking(undefined);
    setShowDetails(false);
    setLoading(false);
    closeDialog();
  }, [closeDialog, setLoading, setNewBooking, setPostProcessFn, setShowDetails]);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setLoading(true);
      setBookings(b => [...b, newBooking as RequiredBooking]);
      // await postBooking(newBooking as Booking);
      postProcessFn();
      handleReset();
    },
    [newBooking, handleReset, postProcessFn, setBookings]
  );

  return (
    <Dialog closeDialog={handleReset} ref={ref} width="400px">
      <Loader loading={loading || !postProcessFn}>
        <form id="gate-new-booking" className="d-flex flex-column" onReset={handleReset} onSubmit={handleSubmit}>
          <h3 className="col-span-4 text-primary-dark">CREATE BOOKING</h3>
          <label htmlFor="booking-number">BOOKING</label>
          <input id="booking-number" type="text" disabled value={newBooking?.bookingNumber ?? ''} />
          {showDetails && (
            <>
              <label htmlFor="customer">CUSTOMER</label>
              <input id="customer" type="text" disabled value={newBooking?.customer?.name ?? ''} />
              <label htmlFor="location">LOCATION</label>
              <input id="location" type="text" disabled value={newBooking?.location?.name ?? ''} />
              <label htmlFor="size-type">SIZE / TYPE</label>
              <input id="size-type" type="text" disabled value={bookingDetails0.iso} />
              <label htmlFor="start-date">START DATE</label>
              <input id="start-date" type="date" disabled value={utcToZonedDate(newBooking?.startDate)} />
            </>
          )}
          <button type="button" className="btn-close col-span-4 py-0" onClick={() => setShowDetails(p => !p)}>
            <span className="material-icons icon-large py-2">more_{showDetails ? 'horiz' : 'vert'}</span>
          </button>
          <label htmlFor="ssl-scac">SSL SCAC</label>
          <SearchableSelect
            options={sslScacs.map(scac => ({ label: scac, value: scac }))}
            value={newBooking?.sslScac}
            onValueSelected={scac => setNewBooking(b => b && { ...b, sslScac: scac })}
          />
          <label htmlFor="expiration-date">EXPIRATION</label>
          <input
            type="date"
            id="expiration-date"
            value={utcToZonedDate(newBooking?.expirationDate)}
            onChange={e => setNewBooking(b => b && { ...b, expirationDate: dateInputToUtc(e.target.value) })}
          />
          <label htmlFor="size-type">QUANTITY</label>
          <div className="p-relative col-span-3 quantity-stepper g-3">
            <button type="button" onClick={() => handleStepChange(-1)}>
              <span className="material-icons">remove</span>
            </button>
            <input
              id="size-type"
              inputMode="numeric"
              value={bookingDetails0.count}
              onChange={handleChangeQuantity}
              onFocus={e => e.target.setSelectionRange(0, e.target.value.length)}
            />
            <button type="button" onClick={() => handleStepChange(1)}>
              <span className="material-icons">add</span>
            </button>
            <label className={`p-absolute l-100 text-info-main `}>&times;&nbsp;{bookingDetails0.iso}</label>
          </div>

          <label htmlFor="notes">NOTES</label>
          <textarea
            id="notes"
            value={newBooking?.notes ?? ''}
            onChange={e => setNewBooking(b => b && { ...b, notes: e.target.value })}
          />
          <div className="d-flex justify-content-between mt-4">
            <div className="d-flex g-2">
              <label htmlFor="carb-compliant">CARB</label>
              <label className="switch">
                <input
                  id="carb-compliant"
                  type="checkbox"
                  disabled={!isReeferOrGenset(bookingDetails0.iso, sizeTypeCatalog)}
                  checked={newBooking?.bookingDetails?.[0]?.carb ?? false}
                  onChange={e =>
                    setNewBooking(b => b && { ...b, bookingDetails: [{ ...bookingDetails0, carb: e.target.checked }] })
                  }
                />
                <span className="slider round" />
              </label>
            </div>
            <div className="d-flex g-4">
              <button type="reset" className="btn-outline col-start-3">
                cancel
              </button>
              <button type="submit" className="btn-primary col-start-4">
                submit
              </button>
            </div>
          </div>
        </form>
      </Loader>
    </Dialog>
  );
};
